import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokers,
  economicGroups,
  nominations,
  policyholderFile,
  settings,
  type EconomicGroupDetail,
  type EconomicGroupEntity,
  type Nomination,
  type NominationMode,
  type Policyholder,
} from '@/shared/api/resources';
import type { Money } from '@/shared/api/types';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatCpfCnpj,
  formatDate,
  formatMoney,
  formatRate,
  moneyInputError,
  moneyToInput,
  parseRate,
  rateInputError,
  rateToInput,
  toMoney,
} from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../../shared/fieldError';
import {
  fimDaNomeacao,
  gruposDeModalidade,
  instrumentosDeNomeacao,
  modosDeNomeacao,
  PENDENCIA_GRUPO_26,
  PENDENCIA_NOMEACAO,
  PENDENCIA_VALORES_DO_GRUPO,
  permissaoDecidirNomeacao,
  TOTAL_DO_GRUPO_PENDENTE,
  permissaoLimiteDoGrupo,
  permissaoModoDaNomeacao,
  permissaoPedirNomeacao,
  rotuloDoInstrumento,
  situacoesDaNomeacao,
  tiposDeNomeacao,
} from '../rotulos';
import { LimiteCalculadoDoGrupo } from './LimiteCalculado';
import { Bloco, Info, InfoGrid, Secao } from './shared';

// Grupo economico como entidade e nomeacao do corretor como pedido (P-TOM-C5). Ver
// docs/ADR/0047-grupo-economico-e-nomeacao.md. A tela mostra o que o servidor devolve
// e esconde o que a permissao nao abre; quem decide e o servidor.

function useInvalidar(id: string) {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ['policyholder', id] });
}

function erroDaApi(erro: Error | null): ApiError | null {
  return erro instanceof ApiError ? erro : null;
}

function dinheiro(valor: Money | null | undefined): string {
  return valor ? formatMoney(valor) : '-';
}

/**
 * Concedido, utilizado e disponivel do grupo inteiro: o servidor os devolve nulos ate a
 * metrica de consumo do grupo ser aprovada (LIM-005, CRE-06). A tela diz que estao
 * pendentes de definicao, e nao zero nem traco.
 */
function totalDoGrupo(valor: Money | null | undefined): string {
  return valor ? formatMoney(valor) : TOTAL_DO_GRUPO_PENDENTE;
}

// --- Grupo Economico ---------------------------------------------------------------

/**
 * Secao Grupo Economico (GetPolicyHolderEconomicGroupInfoList do OnPoint): os grupos
 * de que o tomador participa, com o limite do grupo. Concedido, utilizado e disponivel
 * do grupo aparecem pendentes de definicao (LIM-005, CRE-06). O botao de incluir obedece ao HasChangeEconomicGroup do servidor.
 */
export function GrupoEconomico({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidar(tomador.id);
  const podeLimite = can(permissaoLimiteDoGrupo);
  const [editando, setEditando] = useState<EconomicGroupDetail | 'novo' | null>(null);
  const [documento, setDocumento] = useState('');
  const [nome, setNome] = useState('');
  const [participacao, setParticipacao] = useState('');
  const [holding, setHolding] = useState(false);
  const [limite, setLimite] = useState('');
  const [consultando, setConsultando] = useState<EconomicGroupDetail | null>(null);

  const grupos = useQuery({
    queryKey: ['policyholder', tomador.id, 'economic-groups'],
    queryFn: ({ signal }) => economicGroups.participations(tomador.id, signal),
  });
  const podeTrocar = grupos.data?.change.can_change_group ?? false;

  function abrir(grupo: EconomicGroupDetail | 'novo') {
    setEditando(grupo);
    if (grupo === 'novo') {
      setDocumento('');
      setNome('');
      setParticipacao('');
      setHolding(false);
      setLimite('');
      return;
    }
    setDocumento(grupo.group_document);
    setNome(grupo.group_name);
    setParticipacao(rateToInput(grupo.participation_bps));
    setHolding(grupo.afiancado_holding);
    setLimite(moneyToInput(grupo.group_limit ?? undefined));
  }

  const salvar = useMutation({
    mutationFn: () => {
      const original =
        editando !== null && editando !== 'novo' ? editando.group_limit : null;
      const novoLimite = limite ? toMoney(limite) : null;
      const mudouLimite = (original?.amount ?? null) !== (novoLimite?.amount ?? null);
      const corpo = {
        group_name: nome,
        participation_bps: parseRate(participacao || '0') ?? 0,
        afiancado_holding: holding,
        // O limite e do grupo inteiro: so vai no pedido quando a pessoa o mudou e tem
        // a alcada, para que a edicao da participacao nao toque o limite do grupo.
        ...(podeLimite && mudouLimite ? { group_limit: novoLimite } : {}),
      };
      if (editando === 'novo' || editando === null) {
        return policyholderFile.addEconomicGroup(tomador.id, {
          ...corpo,
          group_document: documento,
        });
      }
      return policyholderFile.updateEconomicGroup(tomador.id, editando.id, corpo);
    },
    onSuccess: () => {
      toast.success('Grupo economico salvo.');
      setEditando(null);
      invalidar();
    },
  });
  const remover = useMutation({
    mutationFn: (grupoId: string) =>
      policyholderFile.removeEconomicGroup(tomador.id, grupoId),
    onSuccess: () => {
      toast.success('Grupo removido.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const apiError = erroDaApi(salvar.error);
  const invalido =
    rateInputError(participacao) !== undefined || moneyInputError(limite) !== undefined;

  return (
    <Secao
      titulo="Grupo Economico"
      descricao="Grupos de que o tomador participa, identificados pelo CPF ou CNPJ de quem os encabeca."
      carregando={grupos.isPending}
      erro={grupos.error}
      acao={
        podeTrocar ? (
          <Button size="sm" onClick={() => abrir('novo')}>
            Adicionar novo grupo
          </Button>
        ) : null
      }
    >
      <Alert tone="info" title="Importante">
        {PENDENCIA_GRUPO_26}
      </Alert>
      {grupos.data && !podeTrocar && grupos.data.change.change_blocked_reason ? (
        <p className="text-2xs text-ink-500">
          {grupos.data.change.change_blocked_reason}
        </p>
      ) : null}
      <DataTable
        columns={[
          {
            key: 'grupo',
            header: 'Grupo',
            render: (g) => (
              <div>
                <p className="font-medium text-ink-900">
                  {g.economic_group?.name || g.group_name || 'Sem nome'}
                </p>
                <p className="text-2xs text-ink-500">
                  {formatCpfCnpj(g.group_document)}
                </p>
              </div>
            ),
          },
          {
            key: 'participacao',
            header: '% Participacao',
            render: (g) => formatRate(g.participation_bps),
          },
          {
            key: 'limite',
            header: 'Limite do grupo',
            render: (g) => dinheiro(g.economic_group?.limit ?? g.group_limit),
          },
          {
            key: 'concedido',
            header: 'Concedido',
            render: (g) => totalDoGrupo(g.granted_limit),
          },
          {
            key: 'utilizado',
            header: 'Utilizado',
            render: (g) => totalDoGrupo(g.used_limit),
          },
          {
            key: 'disponivel',
            header: 'Disponivel',
            render: (g) => totalDoGrupo(g.available_limit),
          },
          {
            key: 'acoes',
            header: 'Acoes',
            render: (g) => (
              <div className="flex gap-1">
                <AcaoComIcone
                  acao="visualizar"
                  rotulo="Dados cadastrais"
                  onClick={() => setConsultando(g)}
                />
                {podeTrocar ? (
                  <AcaoComIcone
                    acao="editar"
                    rotulo="Editar"
                    onClick={() => abrir(g)}
                  />
                ) : null}
                {podeTrocar ? (
                  <AcaoComIcone
                    acao="remover"
                    rotulo="Remover"
                    onClick={() => remover.mutate(g.id)}
                  />
                ) : null}
              </div>
            ),
          },
        ]}
        items={grupos.data?.items ?? []}
        keyOf={(g) => g.id}
        empty={
          <EmptyState icon="users" title="O tomador nao participa de grupo economico" />
        }
      />
      <p className="text-2xs text-ink-500">{PENDENCIA_VALORES_DO_GRUPO}</p>
      <DadosDoGrupo
        tomadorId={tomador.id}
        participacao={consultando}
        onClose={() => setConsultando(null)}
      />
      <Modal
        open={editando !== null}
        onClose={() => setEditando(null)}
        title={editando === 'novo' ? 'Adicionar novo grupo' : 'Editar'}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button
              loading={salvar.isPending}
              disabled={invalido}
              onClick={() => salvar.mutate()}
            >
              Salvar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {salvar.error ? (
            <Alert tone="danger" title="Nao foi possivel salvar">
              {salvar.error.message}
            </Alert>
          ) : null}
          <Field
            label="Documento do Grupo economico"
            placeholder="Digite o CPF ou CNPJ do grupo economico"
            value={documento}
            disabled={editando !== 'novo'}
            error={fieldErrorOf(apiError, 'group_document')}
            onChange={(event) => setDocumento(event.target.value)}
          />
          <Field
            label="Nome do grupo"
            value={nome}
            onChange={(event) => setNome(event.target.value)}
          />
          <Field
            label="% Participacao"
            inputMode="decimal"
            value={participacao}
            error={
              rateInputError(participacao) ??
              fieldErrorOf(apiError, 'participation_bps')
            }
            onChange={(event) => setParticipacao(event.target.value)}
          />
          <label className="flex items-center gap-2 text-xs font-semibold text-ink-700">
            <input
              type="checkbox"
              className="size-4"
              checked={holding}
              onChange={(event) => setHolding(event.target.checked)}
            />
            Afiancado holding
          </label>
          <MoneyField
            label="Limite do grupo (R$)"
            value={limite}
            disabled={!podeLimite}
            hint={
              podeLimite
                ? 'O limite e do grupo inteiro: muda para todos os tomadores dele.'
                : 'O limite do grupo e decisao de credito.'
            }
            error={moneyInputError(limite) ?? fieldErrorOf(apiError, 'group_limit')}
            onValueChange={setLimite}
          />
        </div>
      </Modal>
    </Secao>
  );
}

/**
 * Sub-aba "Dados cadastrais" do grupo (GetPolicyHolderEconomicGroupInfo do OnPoint):
 * o grupo, o limite do grupo com quem o informou, o limite por grupo de modalidades e
 * os tomadores da empresa no grupo.
 */
function DadosDoGrupo({
  tomadorId,
  participacao,
  onClose,
}: {
  tomadorId: string;
  participacao: EconomicGroupDetail | null;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeLimite = can(permissaoLimiteDoGrupo);
  const grupoId = participacao?.economic_group_id ?? '';
  const [limite, setLimite] = useState<string | null>(null);
  const [validadeDoGrupo, setValidadeDoGrupo] = useState('');
  const [modalidade, setModalidade] = useState<{ grupo: string; valor: string } | null>(
    null,
  );

  const grupo = useQuery({
    queryKey: ['policyholder', tomadorId, 'economic-group', grupoId],
    queryFn: ({ signal }) => economicGroups.get(grupoId, signal),
    enabled: participacao !== null && grupoId !== '',
  });

  function atualiza(dados: EconomicGroupEntity) {
    queryClient.setQueryData(
      ['policyholder', tomadorId, 'economic-group', grupoId],
      dados,
    );
    void queryClient.invalidateQueries({
      queryKey: ['policyholder', tomadorId, 'economic-groups'],
    });
  }

  const salvarLimite = useMutation({
    mutationFn: () =>
      economicGroups.update(grupoId, {
        limit: limite ? toMoney(limite) : null,
        // So vai a validade que mudou; vazio limpa a validade (C-LIM).
        ...(validadeDoGrupo !== (grupo.data?.limit_valid_until ?? '')
          ? { limit_valid_until: validadeDoGrupo }
          : {}),
      }),
    onSuccess: (dados) => {
      toast.success('Limite do grupo salvo.');
      setLimite(null);
      atualiza(dados);
    },
  });
  const salvarModalidade = useMutation({
    mutationFn: (entrada: { grupo: string; valor: string }) =>
      economicGroups.setModalityLimit(
        grupoId,
        entrada.grupo,
        entrada.valor ? toMoney(entrada.valor) : null,
      ),
    onSuccess: (dados) => {
      toast.success('Limite por grupo de modalidades salvo.');
      setModalidade(null);
      atualiza(dados);
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const d = grupo.data;
  const usadoPeloTomador = participacao?.policyholder_used_limit ?? null;
  const gruposSemLimite = Object.keys(gruposDeModalidade).filter(
    (chave) =>
      !d?.modality_limits.some(
        (linha) => linha.modality_group === chave && linha.limit,
      ),
  );

  return (
    <Modal
      open={participacao !== null}
      onClose={onClose}
      title="Dados cadastrais do grupo"
      description={
        participacao
          ? `${participacao.group_name || 'Sem nome'} - ${formatCpfCnpj(participacao.group_document)}`
          : undefined
      }
      size="lg"
    >
      <div className="flex flex-col gap-5">
        {grupo.error ? (
          <Alert tone="danger" title="Nao foi possivel carregar">
            {grupo.error.message}
          </Alert>
        ) : null}
        <InfoGrid>
          <Info label="Grupo" value={d?.name || participacao?.group_name} />
          <Info label="Documento do grupo" value={formatCpfCnpj(d?.document ?? '')} />
          <Info label="Criado em" value={d ? formatDate(d.created_at) : ''} />
          <Info
            label="Quem encabeca o grupo"
            value={
              d?.head
                ? `${d.head.legal_name} (tomador desde ${formatDate(d.head.created_at)})`
                : ''
            }
          />
          <Info
            label="Limite do grupo"
            value={d?.limit ? formatMoney(d.limit) : ''}
            hint={
              d?.limit_changed_manually
                ? `Informado por ${d.limit_updated_label}`
                : undefined
            }
          />
          <Info
            label="Alterado manualmente"
            value={d ? (d.limit_changed_manually ? 'Sim' : 'Nao') : ''}
          />
          <Info
            label="Validade do limite do grupo"
            value={d?.limit_valid_until ? formatDate(d.limit_valid_until) : ''}
          />
          <Info
            label="Limite concedido"
            value={d ? totalDoGrupo(d.granted_limit) : ''}
            hint={PENDENCIA_VALORES_DO_GRUPO}
          />
          <Info
            label="Limite utilizado"
            value={d ? totalDoGrupo(d.used_limit) : ''}
            hint={PENDENCIA_VALORES_DO_GRUPO}
          />
          <Info
            label="Limite disponivel"
            value={d ? totalDoGrupo(d.available_limit) : ''}
            hint={PENDENCIA_VALORES_DO_GRUPO}
          />
          <Info
            label="Utilizado pelo tomador no grupo"
            value={usadoPeloTomador ? formatMoney(usadoPeloTomador) : ''}
          />
        </InfoGrid>
        {podeLimite && d ? (
          limite === null ? (
            <div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setLimite(moneyToInput(d.limit ?? undefined));
                  setValidadeDoGrupo(d.limit_valid_until ?? '');
                }}
              >
                Alterar limite do grupo
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <MoneyField
                label="Novo limite do grupo (R$)"
                value={limite}
                hint="Vazio limpa o limite. Vale para todos os tomadores do grupo."
                error={
                  moneyInputError(limite) ??
                  fieldErrorOf(erroDaApi(salvarLimite.error), 'group_limit') ??
                  fieldErrorOf(erroDaApi(salvarLimite.error), 'limit')
                }
                onValueChange={setLimite}
              />
              <Field
                label="Validade do limite do grupo"
                type="date"
                value={validadeDoGrupo}
                hint="Vazio limpa a validade. A regra de validade da empresa diz se ela conta (LIM-Q08)."
                onChange={(event) => setValidadeDoGrupo(event.target.value)}
              />
              <Button
                size="sm"
                loading={salvarLimite.isPending}
                disabled={moneyInputError(limite) !== undefined}
                onClick={() => salvarLimite.mutate()}
              >
                Salvar limite
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setLimite(null)}>
                Cancelar
              </Button>
            </div>
          )
        ) : null}
        <p className="text-2xs text-ink-500">{PENDENCIA_VALORES_DO_GRUPO}</p>

        <Bloco
          titulo="Limites por grupo de modalidades"
          acao={
            podeLimite && d && gruposSemLimite.length > 0 ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  setModalidade({ grupo: gruposSemLimite[0] ?? '', valor: '' })
                }
              >
                Informar limite
              </Button>
            ) : null
          }
        >
          <DataTable
            columns={[
              {
                key: 'grupo',
                header: 'Grupo de modalidades',
                render: (l) => gruposDeModalidade[l.modality_group] ?? l.modality_group,
              },
              {
                key: 'limite',
                header: 'Limite do grupo',
                render: (l) => dinheiro(l.limit),
              },
              {
                key: 'concedido',
                header: 'Concedido',
                render: (l) => totalDoGrupo(l.granted_limit),
              },
              {
                key: 'utilizado',
                header: 'Utilizado',
                render: (l) => totalDoGrupo(l.used_limit),
              },
              {
                key: 'manual',
                header: 'Alterado manualmente',
                render: (l) => (l.limit ? (l.changed_manually ? 'Sim' : 'Nao') : '-'),
              },
              {
                key: 'acoes',
                header: 'Acoes',
                render: (l) =>
                  podeLimite ? (
                    <div className="flex gap-1">
                      <AcaoComIcone
                        acao="editar"
                        rotulo="Editar"
                        onClick={() =>
                          setModalidade({
                            grupo: l.modality_group,
                            valor: moneyToInput(l.limit ?? undefined),
                          })
                        }
                      />
                      {l.limit ? (
                        // Limpar apaga o valor do limite: o x vermelho.
                        <AcaoComIcone
                          icone="close"
                          tom="danger"
                          rotulo="Limpar"
                          onClick={() =>
                            salvarModalidade.mutate({
                              grupo: l.modality_group,
                              valor: '',
                            })
                          }
                        />
                      ) : null}
                    </div>
                  ) : null,
              },
            ]}
            items={d?.modality_limits ?? []}
            keyOf={(l) => l.modality_group}
            empty={
              <p className="text-sm text-ink-400">
                Nenhum limite nem valor por grupo de modalidades.
              </p>
            }
          />
          {modalidade ? (
            <div className="flex flex-wrap items-end gap-2">
              <Select
                label="Grupo de modalidades"
                value={modalidade.grupo}
                options={Object.entries(gruposDeModalidade).map(([value, label]) => ({
                  value,
                  label,
                }))}
                onChange={(event) =>
                  setModalidade({ ...modalidade, grupo: event.target.value })
                }
              />
              <MoneyField
                label="Limite do grupo na modalidade (R$)"
                value={modalidade.valor}
                error={moneyInputError(modalidade.valor)}
                onValueChange={(texto) =>
                  setModalidade({ ...modalidade, valor: texto })
                }
              />
              <Button
                size="sm"
                loading={salvarModalidade.isPending}
                disabled={moneyInputError(modalidade.valor) !== undefined}
                onClick={() => salvarModalidade.mutate(modalidade)}
              >
                Salvar
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setModalidade(null)}>
                Cancelar
              </Button>
            </div>
          ) : null}
        </Bloco>

        <Bloco titulo="Tomadores do grupo">
          <DataTable
            columns={[
              {
                key: 'tomador',
                header: 'Tomador do grupo',
                render: (m) => (
                  <div>
                    <p className="font-medium text-ink-900">{m.policyholder.label}</p>
                    <p className="text-2xs text-ink-500">{formatCpfCnpj(m.document)}</p>
                  </div>
                ),
              },
              {
                key: 'participacao',
                header: '% Participacao',
                render: (m) => formatRate(m.participation_bps),
              },
              {
                key: 'holding',
                header: 'Afiancado holding',
                render: (m) => (m.afiancado_holding ? 'Sim' : 'Nao'),
              },
              {
                key: 'concedido',
                header: 'Concedido',
                render: (m) => dinheiro(m.granted_limit),
              },
              {
                key: 'utilizado',
                header: 'Utilizado',
                render: (m) => dinheiro(m.used_limit),
              },
            ]}
            items={d?.members ?? participacao?.members ?? []}
            keyOf={(m) => m.participation_id ?? m.policyholder.id}
            empty={<p className="text-sm text-ink-400">Nenhum tomador no grupo.</p>}
          />
        </Bloco>

        {grupoId ? <LimiteCalculadoDoGrupo grupoId={grupoId} /> : null}
      </div>
    </Modal>
  );
}

// --- Corretor (nomeacao) -------------------------------------------------------------

const situacaoTone: Record<string, 'warning' | 'success' | 'danger' | 'neutral'> = {
  pendente: 'warning',
  aprovada: 'success',
  recusada: 'danger',
  cancelada: 'neutral',
};

/**
 * Secao Corretor: a nomeacao vigente, o modo da empresa e os pedidos (pedido de
 * nomeacao, nomeacao pela seguradora e pedido de remocao), com a decisao da
 * seguradora. A corretora ve e pede so o que e dela.
 */
export function Corretor({ tomador }: { tomador: Policyholder }) {
  const { can, session } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const daCorretora = session?.broker_scoped === true;
  const podePedir = can(permissaoPedirNomeacao);
  const podeDecidir = can(permissaoDecidirNomeacao) && !daCorretora;
  const podeModo = can(permissaoModoDaNomeacao) && !daCorretora;
  const [pedindo, setPedindo] = useState(false);
  const [decidindo, setDecidindo] = useState<{
    pedido: Nomination;
    decisao: 'aprovar' | 'recusar';
  } | null>(null);

  const lista = useQuery({
    queryKey: ['policyholder', tomador.id, 'nominations'],
    queryFn: ({ signal }) => nominations.list(tomador.id, signal),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['policyholder', tomador.id] });
    void queryClient.invalidateQueries({ queryKey: ['nomination-settings'] });
  }

  const mudarModo = useMutation({
    mutationFn: (modo: NominationMode) => nominations.saveSettings(modo),
    onSuccess: () => {
      toast.success('Modo da nomeacao salvo.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const remover = useMutation({
    mutationFn: (pedido: Nomination) => nominations.removal(tomador.id, pedido.id, ''),
    onSuccess: () => {
      toast.success('Pedido de remocao registrado.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const cancelar = useMutation({
    mutationFn: (pedido: Nomination) => nominations.cancel(tomador.id, pedido.id, ''),
    onSuccess: () => {
      toast.success('Pedido cancelado.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const dados = lista.data;
  const porPedido = dados?.settings.request_enabled ?? false;
  const atual = dados?.current ?? null;

  return (
    <Secao
      titulo="Corretor"
      descricao="Corretor nomeado do tomador e os pedidos de nomeacao e de remocao."
      carregando={lista.isPending}
      erro={lista.error}
      acao={
        podePedir && porPedido && !dados?.has_pending_nomination ? (
          <Button size="sm" onClick={() => setPedindo(true)}>
            Pedir nomeacao
          </Button>
        ) : null
      }
    >
      {daCorretora && dados?.nominated_to_this_broker ? (
        <Alert tone="info" title="Tomador nomeado para sua corretora">
          {tomador.nomination_valid_until
            ? `Nomeacao valida ate ${formatCivilDate(tomador.nomination_valid_until)}.`
            : 'Sem validade de nomeacao registrada.'}
        </Alert>
      ) : null}
      <Bloco titulo="Nomeacao vigente">
        <InfoGrid>
          <Info label="Corretor Nomeado" value={tomador.broker?.label ?? ''} />
          <Info
            label="Data de validade da nomeacao"
            value={formatCivilDate(tomador.nomination_valid_until)}
          />
          <Info
            label="Tipo de nomeacao"
            value={rotuloDoInstrumento(tomador.nomination_instrument ?? '')}
          />
          <Info
            label="Data de nomeacao"
            value={atual?.decided_at ? formatDate(atual.decided_at) : ''}
          />
        </InfoGrid>
        {atual && podePedir && porPedido && !dados?.has_pending_nomination ? (
          <div>
            <Button
              size="sm"
              variant="secondary"
              loading={remover.isPending}
              onClick={() => remover.mutate(atual)}
            >
              Pedir remocao da nomeacao
            </Button>
          </div>
        ) : null}
        <p className="text-2xs text-ink-500">{PENDENCIA_NOMEACAO}</p>
      </Bloco>

      <Bloco titulo="Modo da empresa">
        {podeModo ? (
          <Select
            label="Troca do corretor"
            value={dados?.settings.mode ?? 'pedido'}
            options={modosDeNomeacao}
            disabled={mudarModo.isPending}
            onChange={(event) => mudarModo.mutate(event.target.value as NominationMode)}
          />
        ) : (
          <p className="text-sm text-ink-700">
            {modosDeNomeacao.find((m) => m.value === dados?.settings.mode)?.label ?? ''}
          </p>
        )}
      </Bloco>

      <Bloco titulo="Pedidos">
        {dados?.has_pending_nomination ? (
          <Badge tone="warning">Ha pedido aguardando analise</Badge>
        ) : null}
        <DataTable
          columns={[
            {
              key: 'data',
              header: 'Data da solicitacao',
              render: (p) => formatDate(p.requested_at),
            },
            {
              key: 'tipo',
              header: 'Tipo',
              render: (p) => tiposDeNomeacao[p.kind] ?? p.kind,
            },
            {
              key: 'corretora',
              header: 'Corretora',
              render: (p) => (
                <div>
                  <p className="font-medium text-ink-900">{p.broker.label}</p>
                  <p className="text-2xs text-ink-500">
                    {formatCpfCnpj(p.broker_document)}
                  </p>
                </div>
              ),
            },
            {
              key: 'validade',
              header: 'Validade de nomeacao',
              render: (p) => formatCivilDate(p.valid_until) || '-',
            },
            {
              key: 'instrumento',
              header: 'Tipo de nomeacao',
              render: (p) => rotuloDoInstrumento(p.instrument) || '-',
            },
            {
              key: 'situacao',
              header: 'Situacao',
              render: (p) => (
                <div className="flex flex-col gap-0.5">
                  <Badge tone={situacaoTone[p.status] ?? 'neutral'}>
                    {situacoesDaNomeacao[p.status] ?? p.status}
                  </Badge>
                  {p.ended_reason ? (
                    <span className="text-2xs text-ink-500">
                      {fimDaNomeacao[p.ended_reason] ?? p.ended_reason}
                    </span>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'decisao',
              header: 'Decisao',
              render: (p) =>
                p.decided_at ? (
                  <div className="text-2xs text-ink-600">
                    <p>
                      {p.decided_label} em {formatDate(p.decided_at)}
                    </p>
                    {p.decline_reason ? <p>{p.decline_reason}</p> : null}
                    {p.decision_note ? <p>{p.decision_note}</p> : null}
                  </div>
                ) : (
                  <span className="text-2xs text-ink-500">
                    Pedido por {p.requested_label}
                  </span>
                ),
            },
            {
              key: 'acoes',
              header: 'Acoes',
              render: (p) =>
                p.status === 'pendente' ? (
                  <div className="flex gap-1">
                    {podeDecidir ? (
                      <AcaoComIcone
                        acao="aprovar"
                        rotulo="Aprovar"
                        onClick={() => setDecidindo({ pedido: p, decisao: 'aprovar' })}
                      />
                    ) : null}
                    {podeDecidir ? (
                      <AcaoComIcone
                        acao="recusar"
                        rotulo="Recusar"
                        onClick={() => setDecidindo({ pedido: p, decisao: 'recusar' })}
                      />
                    ) : null}
                    {podePedir ? (
                      <AcaoComIcone
                        acao="cancelar"
                        rotulo="Cancelar pedido"
                        loading={cancelar.isPending}
                        onClick={() => cancelar.mutate(p)}
                      />
                    ) : null}
                  </div>
                ) : null,
            },
          ]}
          items={dados?.items ?? []}
          keyOf={(p) => p.id}
          empty={<EmptyState icon="stamp" title="Nenhum pedido de nomeacao" />}
        />
      </Bloco>

      <PedidoDeNomeacao
        tomador={tomador}
        aberto={pedindo}
        daCorretora={daCorretora}
        onClose={() => setPedindo(false)}
        onDone={() => {
          setPedindo(false);
          invalidar();
        }}
      />
      <DecisaoDoPedido
        tomadorId={tomador.id}
        decisao={decidindo}
        onClose={() => setDecidindo(null)}
        onDone={() => {
          setDecidindo(null);
          invalidar();
        }}
      />
    </Secao>
  );
}

function PedidoDeNomeacao({
  tomador,
  aberto,
  daCorretora,
  onClose,
  onDone,
}: {
  tomador: Policyholder;
  aberto: boolean;
  daCorretora: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [corretora, setCorretora] = useState('');
  const [tipo, setTipo] = useState<'pedido' | 'seguradora'>('pedido');
  const [validade, setValidade] = useState('');
  const [instrumento, setInstrumento] = useState('');
  const [nota, setNota] = useState('');

  const corretoras = useQuery({
    queryKey: ['brokers', 'options'],
    queryFn: ({ signal }) => brokers.list({ limit: 100, stage: 'ativo' }, signal),
    enabled: aberto && !daCorretora,
  });

  const pedir = useMutation({
    mutationFn: () =>
      nominations.request(tomador.id, {
        ...(daCorretora ? {} : { broker_id: corretora, kind: tipo }),
        ...(validade ? { valid_until: validade } : {}),
        ...(instrumento ? { instrument: instrumento } : {}),
        ...(nota ? { note: nota } : {}),
      }),
    onSuccess: () => {
      toast.success('Pedido de nomeacao registrado.');
      setCorretora('');
      setValidade('');
      setInstrumento('');
      setNota('');
      onDone();
    },
  });
  const apiError = erroDaApi(pedir.error);

  return (
    <Modal
      open={aberto}
      onClose={onClose}
      title="Pedir nomeacao"
      description={tomador.legal_name}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={pedir.isPending}
            disabled={!daCorretora && corretora === ''}
            onClick={() => pedir.mutate()}
          >
            Enviar pedido
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {pedir.error ? (
          <Alert tone="danger" title="Nao foi possivel registrar o pedido">
            {pedir.error.message}
          </Alert>
        ) : null}
        {daCorretora ? (
          <p className="text-sm text-ink-600">
            O pedido e para a sua corretora e espera a decisao da seguradora.
          </p>
        ) : (
          <>
            <Select
              label="Corretora a nomear"
              value={corretora}
              placeholder="Escolha a corretora"
              options={(corretoras.data?.items ?? []).map((item) => ({
                value: item.id,
                label: item.legal_name,
              }))}
              hint="Apenas corretores ativos aparecem aqui."
              error={fieldErrorOf(apiError, 'broker_id')}
              onChange={(event) => setCorretora(event.target.value)}
            />
            <Select
              label="Tipo do pedido"
              value={tipo}
              options={[
                { value: 'pedido', label: 'Pedido de nomeacao' },
                { value: 'seguradora', label: 'Nomeacao pela seguradora' },
              ]}
              onChange={(event) =>
                setTipo(event.target.value as 'pedido' | 'seguradora')
              }
            />
          </>
        )}
        <Field
          label="Validade da nomeacao"
          type="date"
          value={validade}
          error={fieldErrorOf(apiError, 'valid_until')}
          onChange={(event) => setValidade(event.target.value)}
        />
        <Select
          label="Tipo de nomeacao"
          value={instrumento}
          placeholder="Nao informado"
          options={instrumentosDeNomeacao}
          error={fieldErrorOf(apiError, 'instrument')}
          onChange={(event) => setInstrumento(event.target.value)}
        />
        <Textarea
          label="Observacao do pedido"
          value={nota}
          error={fieldErrorOf(apiError, 'note')}
          onChange={(event) => setNota(event.target.value)}
        />
      </div>
    </Modal>
  );
}

function DecisaoDoPedido({
  tomadorId,
  decisao,
  onClose,
  onDone,
}: {
  tomadorId: string;
  decisao: { pedido: Nomination; decisao: 'aprovar' | 'recusar' } | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [motivo, setMotivo] = useState('');
  const [nota, setNota] = useState('');
  const recusa = decisao?.decisao === 'recusar';

  const motivos = useQuery({
    queryKey: ['decline-reasons', 'nomeacao'],
    queryFn: ({ signal }) => settings.declineReasons({ scope: 'nomeacao' }, signal),
    enabled: recusa,
    staleTime: 5 * 60_000,
  });

  const decidir = useMutation({
    mutationFn: () =>
      nominations.decide(tomadorId, decisao?.pedido.id ?? '', {
        decision: decisao?.decisao ?? 'aprovar',
        ...(recusa && motivo ? { decline_reason_id: motivo } : {}),
        ...(nota ? { note: nota } : {}),
      }),
    onSuccess: () => {
      toast.success(recusa ? 'Pedido recusado.' : 'Pedido aprovado.');
      setMotivo('');
      setNota('');
      onDone();
    },
  });
  const apiError = erroDaApi(decidir.error);
  const pedido = decisao?.pedido;

  return (
    <Modal
      open={decisao !== null}
      onClose={onClose}
      title={recusa ? 'Recusar pedido' : 'Aprovar pedido'}
      description={
        pedido
          ? `${tiposDeNomeacao[pedido.kind] ?? pedido.kind} - ${pedido.broker.label}`
          : undefined
      }
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant={recusa ? 'danger' : 'primary'}
            loading={decidir.isPending}
            onClick={() => decidir.mutate()}
          >
            {recusa ? 'Recusar' : 'Aprovar'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {decidir.error ? (
          <Alert tone="danger" title="Nao foi possivel decidir">
            {decidir.error.message}
          </Alert>
        ) : null}
        {!recusa && pedido ? (
          <p className="text-sm text-ink-600">
            {pedido.kind === 'remocao'
              ? 'A nomeacao vigente e encerrada e o corretor sai do cadastro do tomador.'
              : 'A corretora, a validade e o tipo vao para o cadastro do tomador, e a nomeacao anterior e encerrada.'}
          </p>
        ) : null}
        {recusa ? (
          <Select
            label="Motivo da recusa"
            value={motivo}
            placeholder="Escolha o motivo padronizado"
            options={(motivos.data ?? [])
              .filter((item) => item.active)
              .map((item) => ({ value: item.id, label: item.label }))}
            hint="Motivos da esteira Nomeacao, cadastrados em Configuracoes. Sem motivo, escreva a justificativa."
            error={fieldErrorOf(apiError, 'decline_reason_id')}
            onChange={(event) => setMotivo(event.target.value)}
          />
        ) : null}
        {recusa ? (
          <Textarea
            label="Justificativa"
            value={nota}
            error={fieldErrorOf(apiError, 'note')}
            onChange={(event) => setNota(event.target.value)}
          />
        ) : (
          <Textarea
            label="Observacao da decisao"
            value={nota}
            error={fieldErrorOf(apiError, 'note')}
            onChange={(event) => setNota(event.target.value)}
          />
        )}
      </div>
    </Modal>
  );
}
